import { gsap } from "gsap";
import { Graphics, Sprite, Texture } from "pixi.js";

import { AbstractBitmapText } from "../../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../../../abstracts/modules/AbstractView";
import { AbstractScreen } from "../../../../../abstracts/modules/screens/AbstractScreen";
import { generateScaledSprite } from "../../../../../abstracts/utils/GraphicUtils";
import {
    fadeAndStopSound,
    playNextBackgroundMusic,
    playSound,
    startAmbSounds,
    stopAmbSounds,
} from "../../../../../abstracts/utils/SoundUtils";
import { displayConfig } from "../../../../UCConfig";
import { UgliestCatch } from "../../../../UgliestCatch";
import { UCFontsNames, UCSoundNames, UCSpineNames } from "../../../../assetsLoader/UCAssetsNames";
import { UCGameData, UCInitData } from "../../../../types/UCInterfaces";

export class UCFreespinIntroScreen extends AbstractScreen {
    protected backgroundImage: Sprite;
    protected backgroundSpine: AbstractSpine;
    protected spinsNameSprite: Sprite;
    protected spinsTextSprite: Sprite;

    public fsLeftText: AbstractBitmapText;

    constructor(parentContainer: AbstractView, name: string = `UCFreespinIntroScreen`) {
        super(name, parentContainer);
    }

    protected getScreenCustomName(): string {
        return `Freespin Intro`;
    }

    protected getTextureName(): string {
        return `FS_INTRO`;
    }

    protected getSpineName(): string {
        return UCSpineNames.FS_INTRO;
    }

    protected getSpinsName(level: number): string {
        switch (level) {
            case 4:
                return `hawgSpins`;
            case 5:
                return `honeySpins`;
            default:
                return `lunkerSpins`;
        }
    }

    protected init(): void {
        super.init();

        this.addBackgroundImage();
        this.addContinueButton();
        this.addCustomText();
        this.addSpinsNameAndText();
    }

    protected addBackgroundImage(): void {
        const bg: Graphics = new Graphics();
        bg.beginFill(0x000000);
        bg.drawRect(0, 0, displayConfig.backgroundWidth, displayConfig.backgroundHeight);
        bg.endFill();
        bg.alpha = 0.7;
        bg.name = `introGraphics`;
        this.addChild(bg);

        this.backgroundSpine = new AbstractSpine(this.getSpineName());
        this.backgroundSpine.scale.set(1);
        this.backgroundSpine.position.set(837, 837);

        this.addChild(this.backgroundSpine);
    }

    protected addSpinsNameAndText(): void {
        const gameData: UCInitData | UCGameData = UgliestCatch.model.data,
            spinsLevel: number = gameData.extraPositionsOpen.filter((item) => item).length,
            fsLeft: number = gameData.freespinsLeft;

        this.spinsNameSprite = generateScaledSprite(this.getSpinsName(spinsLevel));
        this.spinsNameSprite.anchor.set(0.5);
        this.spinsNameSprite.position.set(837, 630);

        this.spinsTextSprite = generateScaledSprite(fsLeft === 1 ? `spinText` : `spinsText`);
        this.spinsTextSprite.anchor.set(0.5);
        this.spinsTextSprite.position.set(837, 690);

        this.addChild(this.spinsNameSprite, this.spinsTextSprite);
    }

    protected addContinueButton(): void {
        this.continueButton.addClickCallback(() => this.continueButtonClickCallback());
        this.continueButton.position.set(837 - this.continueButton.width / 2, 1058);
        this.continueButton.enable(true);
    }

    protected continueButtonClickCallback(): void {
        this.onContinueBtnClick();
        this.selfRemoveFromParent();

        this.backgroundSpine?.reset();

        this.removeChild(this.continueButton);
        this.spinsNameSprite.visible = false;
        this.spinsTextSprite.visible = false;

        playSound({ name: UCSoundNames.CONTINUE_BUTTON });
        playSound({ name: UCSoundNames.FS_ON_CONTINUE });
        startAmbSounds();

        fadeAndStopSound({ name: this.getIntroLoopSoundName(), duration: 0.5, from: 1, to: 0 });
        if (!this.name.toLowerCase().includes(`outro`.toLowerCase())) {
            playNextBackgroundMusic();
        }
    }

    // public override onContinueBtnClick: Function;

    protected addCustomText(): void {
        this.addFreespinsLeftText();
    }

    protected addFreespinsLeftText() {
        this.fsLeftText = new AbstractBitmapText(`ways`, {
            fontName: UCFontsNames.UC_WAYS,
            fontSize: 12,
        });
        this.fsLeftText.x = 837;
        this.fsLeftText.y = 560;

        this.addChild(this.fsLeftText);
    }

    protected getFsLeftDefaultText(): string {
        return `Freespins ${UgliestCatch.apiPlugin.translations.translate(`won`)}:`;
    }

    public override getGsapAnimation(fsLeft: number = 0): gsap.core.Timeline {
        // stopBackgroundMusic();
        const gameData: UCInitData | UCGameData = UgliestCatch.model.data;
        const spinsLevel: number = gameData.extraPositionsOpen.filter((item) => item).length;

        this.fsLeftText.updateValue(`${fsLeft}`);

        this.spinsTextSprite.texture = Texture.from(fsLeft === 1 ? `spinText` : `spinsText`);
        this.spinsTextSprite.visible = true;

        this.spinsNameSprite.texture = Texture.from(this.getSpinsName(spinsLevel));
        this.spinsNameSprite.visible = true;

        const tl = gsap.timeline({ autoRemoveChildren: true });

        const showScreenTween = gsap.fromTo(
            this,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.3,
                onStart: () => {
                    this.addChild(this.continueButton);
                    playSound({ name: this.getIntroStartSoundName() });
                    playSound({ name: this.getIntroLoopSoundName(), repeat: -1 });
                    stopAmbSounds();
                },
                onComplete: () => {
                    this.backgroundSpine.getSpineTween(`Loop`, true);
                },
            }
        );

        tl.add(showScreenTween);

        return tl;
    }

    protected getIntroStartSoundName(): string {
        return UCSoundNames.FS_INTRO_START;
    }

    protected getIntroLoopSoundName(): string {
        return UCSoundNames.FS_INTRO_LOOP;
    }
}
